import * as signalR from '@microsoft/signalr';
import { getBaseUrls } from './apiConfig';
import { apiService } from './apiService';
import { authService } from '../../features/auth/services/authService';
import { totemModeService } from './totemModeService';
import { bannerService, TelaBanner } from '../../features/banners/services/bannerService';
import {
  layoutStyleFromTelaConfiguracoes,
  mergePanfletoContent,
  parsePanfletoContentFromApiConfig,
  TIPO_TO_LAYOUT,
} from '../../features/banners/services/bannerSyncService';
import { Screen, LayoutType, LayoutStyle } from '../types';
import { detectTV } from '../utils/deviceDetection';

/**
 * Tipos de eventos que podem ser recebidos via SignalR
 */
export type WebSocketEventType = 
  | 'screen_updated'      // Tela foi atualizada
  | 'televisao_updated'   // Televisão foi atualizada
  | 'sequence_updated';    // Sequência foi atualizada

/**
 * Payload de evento de atualização de tela
 */
export interface ScreenUpdatedEvent {
  type: 'screen_updated';
  screenId: number;
  data: any; // Dados completos da tela atualizada
}

/**
 * Payload de evento de atualização de televisão
 */
export interface TelevisaoUpdatedEvent {
  type: 'televisao_updated';
  televisaoId: number;
  data: any; // Dados completos da televisão atualizada
}

/**
 * Payload de evento de atualização de sequência
 */
export interface SequenceUpdatedEvent {
  type: 'sequence_updated';
  nomeSequencia: string;
  data: any; // Dados completos da sequência atualizada
}

export type WebSocketEvent = ScreenUpdatedEvent | TelevisaoUpdatedEvent | SequenceUpdatedEvent;

/**
 * Callback para eventos SignalR
 */
export type WebSocketEventHandler = (event: WebSocketEvent) => void;

/**
 * Opções de conexão SignalR
 */
interface WebSocketOptions {
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

/**
 * Serviço centralizado para gerenciar conexões SignalR
 * 
 * Funcionalidades:
 * - Conexão única compartilhada (singleton)
 * - Reconexão automática em caso de desconexão
 * - Gerenciamento de grupos (telas, televisões, sequências)
 */
class WebSocketService {
  private connection: signalR.HubConnection | null = null;
  private hubUrl: string | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 3000; // 3 segundos
  private isConnecting = false;
  private isManualClose = false;
  private eventHandlers: Set<WebSocketEventHandler> = new Set();
  private subscriptions: Set<string> = new Set(); // IDs de telas/televisões/sequências sendo monitoradas
  // Callbacks diretos por screenId - quando receber TelaAtualizada, faz GET e chama callback
  private screenUpdateCallbacks: Map<number, (screen: Screen, telaAPI: TelaBanner) => void> = new Map();
  
  // Callbacks para telas da televisão - quando receber TelaAtualizada de uma tela que está em uma televisão
  // Map<screenId, callback>
  private televisaoScreenUpdateCallbacks: Map<number, (screen: Screen, telaAPI: TelaBanner) => void> = new Map();
  
  // Callbacks para atualização de televisão - quando receber TelevisaoAtualizada, chama callback com dados recebidos
  // Map<televisaoId, callback>
  private televisaoUpdateCallbacks: Map<number, (data: any) => void> = new Map();
  
  // Callbacks para atualização de preços de produtos - quando receber ProdutoAtualizado
  // Map<screenId, callback> - callback recebe array de produtoIds que precisam ter preços atualizados
  private priceUpdateCallbacks: Map<number, (produtoIds: number[]) => void> = new Map();
  
  // Para TVs e Totem: verificação periódica da conexão (heartbeat)
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isTV: boolean = false;
  private lastHeartbeatTime: number = 0;
  
  // Timer de renovação de token e reconexão preventiva a cada 2 horas
  private tokenRenewalTimer: NodeJS.Timeout | null = null;
  private readonly TOKEN_RENEWAL_INTERVAL_MS = 7200000; // 2 horas em milissegundos (2 * 60 * 60 * 1000)
  private isTokenRenewalTimerActive: boolean = false; // Flag para evitar múltiplas inicializações
  private tokenRenewalTimerLock: boolean = false; // Lock adicional para evitar condições de corrida
  private pendingReconnectAfterTokenRenewal: boolean = false;
  private isIntentionalReconnect: boolean = false;
  private isForceReconnecting: boolean = false; // Lock para evitar múltiplas chamadas simultâneas de forceReconnect
  private lastSuccessfulConnectionTime: number = 0; // Timestamp da última conexão bem-sucedida
  private readonly MIN_TIME_BEFORE_FORCE_RECONNECT = 10000; // 10 segundos - não reconectar se acabou de conectar (aumentado para produção)
  private isRecentlyConnected: boolean = false; // Flag para indicar se acabou de conectar (mais confiável que tempo)
  
  // Verificação periódica de saúde da conexão e token (para modo totem - TODOS os dispositivos)
  private connectionHealthCheckInterval: NodeJS.Timeout | null = null;
  private readonly CONNECTION_HEALTH_CHECK_INTERVAL_MS = 300000; // 5 minutos - verificar saúde da conexão e token
  private lastSuccessfulTokenRenewal: number = 0; // Timestamp da última renovação bem-sucedida
  private isHealthCheckActive: boolean = false; // Flag para evitar múltiplas instâncias do health check

  /**
   * Constrói a URL do SignalR Hub baseada na configuração da API
   * IMPORTANTE: 
   * - TV não logada (sem token): conecta SEM empresa no path, mantendo /r/ se necessário
   * - Sessões logadas: conecta COM empresa do localStorage: /r/{empresa}/hubs/banner
   */
  private getHubUrl(): string | null {
    const SERVERS = [
      process.env.REACT_APP_API_SERVER_1 || 'http://localhost:5000',
      process.env.REACT_APP_API_SERVER_2 || 'http://localhost:5000'
    ].filter((server): server is string => Boolean(server));
    
    if (SERVERS.length === 0) {
      console.warn('[SignalR] ⚠️ Nenhuma URL base configurada');
      return null;
    }

    const server = SERVERS[0];
    
    // Se for localhost, usa direto sem empresa
    if (server.includes('localhost') || server.includes('127.0.0.1')) {
      return `${server}/hubs/banner`;
    }
    
    // Para produção: verificar se há token (sessão logada)
    const token = apiService.getToken();
    
    if (!token) {
      // TV não logada: manter padrão /r/ mas SEM empresa
      // O grupo CodigoFalso_{codigoFalso} é global e não depende de empresa
      const baseUrl = server.endsWith('/r') ? server : `${server}/r`;
      return `${baseUrl}/hubs/banner`;
    }
    
    // Sessão logada: usar empresa do localStorage
    const empresa = apiService.getCompany();
    if (empresa && empresa.trim() !== '') {
      const baseUrl = server.endsWith('/r') ? server : `${server}/r`;
      return `${baseUrl}/${empresa}/hubs/banner`;
    }
    
    // Fallback: sem empresa (não deveria acontecer se houver token)
    const baseUrl = server.endsWith('/r') ? server : `${server}/r`;
    return `${baseUrl}/hubs/banner`;
  }

  /**
   * Conecta ao servidor SignalR
   */
  connect(options: WebSocketOptions = {}): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
        resolve();
        return;
      }

      // CRÍTICO: Verificar se já está conectando ou se já está conectado
      if (this.isConnecting) {
        // Aguardar um pouco e verificar novamente
        setTimeout(() => {
          if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            resolve();
          } else {
            // Se ainda não conectou, rejeitar para evitar múltiplas tentativas
            reject(new Error('Conexão já em andamento'));
          }
        }, 1000);
        return;
      }

      // Verificar se já está conectado
      if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
        resolve();
        return;
      }

      this.isConnecting = true;
      this.isManualClose = false;
      
      const hubUrl = this.getHubUrl();
      if (!hubUrl) {
        this.isConnecting = false;
        reject(new Error('URL do SignalR Hub não configurada'));
        return;
      }

      this.hubUrl = hubUrl;
      console.log(`[SignalR] 🔌 Conectando ao Hub: ${hubUrl}`);
      
      // Detectar se é TV física
      this.isTV = detectTV();

      try {
        // Obter token de autenticação
        const token = apiService.getToken();
        
        // Configuração de reconexão automática
        // Para TVs: intervalos de 1 minuto
        const reconnectConfig = this.isTV 
          ? {
              // Para TVs: primeira tentativa imediata, depois a cada 1 minuto
              nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
                const count = retryContext.previousRetryCount;
                if (count === 0) return 0;      // Reconexão imediata na primeira tentativa
                return 60000; // Depois mantém 1 minuto (60000ms) entre tentativas
              }
            }
          : {
              // Para outros dispositivos: intervalos padrão
              nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
                const count = retryContext.previousRetryCount;
                return count === 0 ? 0 : count === 1 ? 2000 : count === 2 ? 10000 : 30000;
              }
            };
        
        // Criar conexão SignalR
        // IMPORTANTE: O backend agora faz autenticação manual do token JWT no SignalR
        // O token é extraído da query string (?access_token=TOKEN) ou do header Authorization (Bearer TOKEN)
        // O accessTokenFactory usa getValidAccessToken() que verifica expiração e renova automaticamente
        // Isso garante que:
        // 1. O token seja passado corretamente na conexão inicial
        // 2. Renovações de token sejam refletidas automaticamente em reconexões
        // 3. O SignalR use sempre o token mais recente e válido
        // 4. Token seja renovado preventivamente antes de expirar
        //
        // CONFIGURAÇÃO DE TIMEOUT (alinhado com backend):
        // Backend: KeepAliveInterval = 30s (envia ping a cada 30s)
        // Backend: ClientTimeoutInterval = 2min (timeout se não receber resposta em 2min)
        // Frontend: serverTimeoutInMilliseconds = 3min (180000ms) - maior que o intervalo de ping para aguardar os pings do servidor
        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: async () => {
              // Usar getValidAccessToken() para garantir token válido (renova se necessário)
              const validToken = await authService.getValidAccessToken();
              return validToken || '';
            },
            // SignalR gerencia automaticamente o transporte (WebSocket, Server-Sent Events, Long Polling)
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect(reconnectConfig)
          .configureLogging(signalR.LogLevel.Warning)
          .build();
        
        // Configurar timeout do servidor (alinhado com backend)
        // Backend envia ping a cada 30s, então o cliente precisa aguardar pelo menos 2-3 minutos
        // para não desconectar antes de receber os pings
        this.connection.serverTimeoutInMilliseconds = 180000; // 3 minutos (180000ms)

        // Configurar handlers de eventos do SignalR
        this.setupSignalRHandlers(options);

        // Iniciar conexão
        this.connection.start()
          .then(() => {
            this.isConnecting = false;
            this.reconnectAttempts = 0;
            this.lastHeartbeatTime = Date.now();
            this.lastSuccessfulConnectionTime = Date.now();
            this.isRecentlyConnected = true;
            
            // Limpar flag após um tempo
            setTimeout(() => {
              this.isRecentlyConnected = false;
            }, this.MIN_TIME_BEFORE_FORCE_RECONNECT);
            
            // Re-subscribir a todas as subscriptions ativas
            this.resubscribeAll();
            
            // Para TVs: iniciar verificação periódica (heartbeat)
            if (this.isTV) {
              this.startHeartbeat();
            }
            
            // Para modo totem (TODOS os dispositivos): iniciar verificação de saúde da conexão
            if (totemModeService.isTotemMode()) {
              this.startConnectionHealthCheck();
            }
            
            // Iniciar timer de renovação de token e reconexão preventiva (2 horas)
            // CRÍTICO: Sempre iniciar para garantir renovação contínua em modo totem
            this.startTokenRenewalTimer();
            
            // Registrar timestamp da conexão inicial como renovação bem-sucedida
            this.lastSuccessfulTokenRenewal = Date.now();
            
            resolve();
          })
          .catch((error) => {
            console.error('[SignalR] Erro ao conectar:', error);
            this.isConnecting = false;
            reject(error);
          });
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Configura handlers de eventos do SignalR
   */
  private setupSignalRHandlers(options: WebSocketOptions): void {
    if (!this.connection) {
      console.error('[SignalR] ❌ Conexão não disponível para configurar handlers');
      return;
    }


    // ============================================
    // HANDLER 1: TelaAtualizada (atualização de TELA individual)
    // ============================================
    // IMPORTANTE: Este handler é para quando uma TELA é atualizada
    // O backend chama: await NotificarTelaAsync(command.TelaId, "TelaAtualizada", result)
    // Este handler chama callbacks de:
    // - Telas normais (screenUpdateCallbacks)
    // - Telas que estão em televisões (televisaoScreenUpdateCallbacks)
    this.connection.on('TelaAtualizada', async (screenIdOrObject: any, data: any) => {
      // Extrair screenId
      let screenId: number;
      if (typeof screenIdOrObject === 'number') {
        screenId = screenIdOrObject;
      } else if (screenIdOrObject && typeof screenIdOrObject === 'object') {
        screenId = screenIdOrObject.telaId || screenIdOrObject.id;
      } else {
        console.error('[SignalR] ❌ Formato inválido de screenId:', screenIdOrObject);
        return;
      }


      // LÓGICA: Quando uma TELA é atualizada, notificar:
      // 1. Callbacks de telas normais (DisplayPage)
      // 2. Callbacks de telas que estão em televisões (TelevisaoDisplayPage)
      const callbackTelaNormal = this.screenUpdateCallbacks.get(screenId);
      const callbackTelaTelevisao = this.televisaoScreenUpdateCallbacks.get(screenId);

      // Foto de fundo do panfleto vem de endpoint dedicado, fora do ds_configuracoes.
      // Ao receber TelaAtualizada, invalidar cache para forçar revalidação da imagem.
      bannerService.invalidarCacheFotoTela(screenId);
      
      // Se não houver nenhum callback, não fazer nada
      if (!callbackTelaNormal && !callbackTelaTelevisao) {
        return;
      }

      try {
        // CRÍTICO: Aguardar um pouco para garantir que o backend já processou a atualização
        // Isso evita pegar dados em cache do servidor
        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms de delay
        
        // PASSO 1: Fazer GET SEM CACHE com retry para garantir dados sempre frescos
        // Usa timestamp 't' na URL e headers de cache-control (não '_nocache')
        let tela: TelaBanner | undefined;
        let tentativas = 0;
        const maxTentativas = 3;
        
        while (tentativas < maxTentativas) {
          try {
            // Fazer GET com timestamp único a cada tentativa para quebrar cache
            const telasAPI = await bannerService.listarTelas(screenId, true);
            tela = telasAPI.find(t => t.id === screenId);
            
            if (tela) {
              break;
            }
          } catch (error) {
            console.warn(`[SignalR] ❌ Erro na tentativa ${tentativas + 1}:`, error);
          }
          
          tentativas++;
          if (tentativas < maxTentativas) {
            // Aguardar mais um pouco antes de tentar novamente
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
        
        if (!tela) {
          console.warn(`[SignalR] ❌ Tela ${screenId} não encontrada após ${maxTentativas} tentativas`);
          return;
        }

        // PASSO 2: Mesmo que apiTelaToLocalScreen / Display (jsonToLayoutStyle + reforço panfleto no JSON)
        let layoutStyle: LayoutStyle | undefined = layoutStyleFromTelaConfiguracoes(tela.configuracoes);
        if (!layoutStyle && tela.configuracoes && tela.configuracoes.trim() !== '' && tela.configuracoes.trim() !== 'string') {
          try {
            const config = JSON.parse(tela.configuracoes);
            const ls: any = {};

            if (config.productName) {
              ls.productName = {
                color: config.productName.color || '#000000',
                fontSize: config.productName.fontSize ?? 16,
                fontFamily: config.productName.fontFamily || 'Arial, sans-serif',
                lineHeight: config.productName.lineHeight ?? 1.2,
                letterSpacing: config.productName.letterSpacing ?? 0,
                fontWeight: config.productName.fontWeight ?? 400,
                verticalStretch: config.productName.verticalStretch ?? 100,
                showCurrency: config.productName.showCurrency !== undefined ? config.productName.showCurrency : true,
              };
            }

            if (config.price) {
              ls.price = {
                color: config.price.color || '#000000',
                fontSize: config.price.fontSize ?? 16,
                fontFamily: config.price.fontFamily || 'Arial, sans-serif',
                lineHeight: config.price.lineHeight ?? 1.2,
                letterSpacing: config.price.letterSpacing ?? 0,
                fontWeight: config.price.fontWeight ?? 400,
                verticalStretch: config.price.verticalStretch ?? 100,
                showCurrency: config.price.showCurrency !== undefined ? config.price.showCurrency : true,
              };
            }

            if (config.unit) {
              ls.unit = {
                color: config.unit.color || '#000000',
                fontSize: config.unit.fontSize ?? 16,
                fontFamily: config.unit.fontFamily || 'Arial, sans-serif',
                lineHeight: config.unit.lineHeight ?? 1.2,
                letterSpacing: config.unit.letterSpacing ?? 0,
                fontWeight: config.unit.fontWeight ?? 400,
                verticalStretch: config.unit.verticalStretch ?? 100,
                showCurrency: config.unit.showCurrency !== undefined ? config.unit.showCurrency : true,
              };
            }

            if (config.productContainer) {
              ls.productContainer = { ...config.productContainer };
            }

            if (config.description) {
              ls.description = { ...config.description };
            }

            if (config.category) {
              ls.category = { ...config.category };
            }

            if (config.validUntil) {
              ls.validUntil = { ...config.validUntil };
            }

            if (config.priceBox) {
              ls.priceBox = {
                enabled: config.priceBox.enabled ?? false,
                backgroundColor: config.priceBox.backgroundColor || '#ffffff',
                borderColor: config.priceBox.borderColor || '#000000',
                borderWidth: config.priceBox.borderWidth ?? 0,
                borderRadius: config.priceBox.borderRadius ?? 8,
                paddingTop: config.priceBox.paddingTop ?? 4,
                paddingRight: config.priceBox.paddingRight ?? 8,
                paddingBottom: config.priceBox.paddingBottom ?? 4,
                paddingLeft: config.priceBox.paddingLeft ?? 8,
                marginTop: config.priceBox.marginTop ?? 0,
                marginRight: config.priceBox.marginRight ?? 0,
                marginBottom: config.priceBox.marginBottom ?? 0,
                marginLeft: config.priceBox.marginLeft ?? 0,
              };
            }

            const panfletoRaw = parsePanfletoContentFromApiConfig(config);
            if (panfletoRaw) {
              ls.panfletoContent = mergePanfletoContent(panfletoRaw);
            }

            if (Object.keys(ls).length > 0) {
              layoutStyle = ls as LayoutStyle;
            }
          } catch (e) {
            console.warn(`[SignalR] Erro ao parsear configuracoes:`, e);
          }
        }
        
        // PASSO 3: Criar Screen com dados FRESCOS
        const screen: Screen = {
          id: screenId,
          name: tela.nome || 'Tela sem nome',
          layoutId: (tela.tipo !== undefined ? TIPO_TO_LAYOUT[tela.tipo] : 'simple2col') as LayoutType,
          promotions: [],
          isActive: tela.ativo ?? false,
          createdAt: new Date().toISOString(),
          description: tela.descricao,
          linkedProducts: [],
          backgroundColor: '#ffffff',
          backgroundImage: undefined,
          showHeader: true,
          duration: 30,
          layoutStyle: layoutStyle // DIRETO DA API, SEM CACHE
        };

        // PASSO 4: Aplicar IMEDIATAMENTE
        // Chamar callback da tela normal (DisplayPage) - se existir
        if (callbackTelaNormal) {
          callbackTelaNormal(screen, tela);
        }
        
        // Chamar callback da tela que está em televisão (TelevisaoDisplayPage) - se existir
        // IMPORTANTE: Quando uma tela é atualizada, a televisão que usa essa tela também precisa atualizar
        if (callbackTelaTelevisao) {
          callbackTelaTelevisao(screen, tela);
        }
        
        // NOTA: Renovação de token é feita automaticamente pelo timer de 2h e pelo health check
        // Não é necessário chamar RenovarToken() aqui
      } catch (error) {
        console.error(`[SignalR] ❌ Erro ao atualizar tela ${screenId}:`, error);
      }
    });

    // ============================================
    // HANDLER 2: TelevisaoAtualizada (atualização de TELEVISÃO)
    // ============================================
    // IMPORTANTE: Este handler é para quando a CONFIGURAÇÃO DA TELEVISÃO é atualizada
    // (ordem das telas, telas adicionadas/removidas, dias ativos, tempos, etc)
    // FUNCIONA IGUAL AO HANDLER DE TELA: faz GET da televisão para buscar dados atualizados
    // 
    // O backend chama: await NotificarTelevisaoAsync(cdTelevisao, "TelevisaoAtualizada", data)
    // Então o método no Hub deve ser "TelevisaoAtualizada" e recebe (televisaoId, data) ou apenas (data)
    
    // Handler principal - pode receber (televisaoId, data) ou apenas (data) com televisaoId dentro
    this.connection.on('TelevisaoAtualizada', async (param1: any, param2?: any) => {
      // Extrair televisaoId
      let televisaoId: number | undefined;
      if (typeof param1 === 'number') {
        televisaoId = param1;
      } else if (param1 && typeof param1 === 'object') {
        televisaoId = param1.cdTelevisao || param1.televisaoId || param1.id;
      } else {
        console.error('[SignalR] ❌ Formato inválido de televisaoId:', param1);
        return;
      }

      if (!televisaoId) {
        console.error('[SignalR] ❌ Não foi possível extrair televisaoId do evento');
        return;
      }

      // LÓGICA: Quando uma TELEVISÃO é atualizada, notificar callbacks registrados
      const callbackTelevisao = this.televisaoUpdateCallbacks.get(televisaoId);
      
      // Se não houver callback, não fazer nada
      if (!callbackTelevisao) {
        return;
      }

      try {
        // CRÍTICO: Aguardar um pouco para garantir que o backend já processou a atualização
        // Isso evita pegar dados em cache do servidor
        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms de delay
        
        // PASSO 1: Fazer GET da televisão (sem parâmetros adicionais como t ou nocache)
        let televisao: any | undefined;
        let tentativas = 0;
        const maxTentativas = 3;
        
        while (tentativas < maxTentativas) {
          try {
            // Fazer GET da televisão
            const { televisaoService } = await import('../../features/televisao/services/televisaoService');
            televisao = await televisaoService.buscarTelevisao(televisaoId);
            
            if (televisao) {
              break;
            }
          } catch (error) {
            console.warn(`[SignalR] ❌ Erro na tentativa ${tentativas + 1}:`, error);
          }
          
          tentativas++;
          if (tentativas < maxTentativas) {
            // Aguardar mais um pouco antes de tentar novamente
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
        
        if (!televisao) {
          console.warn(`[SignalR] ❌ Televisão ${televisaoId} não encontrada após ${maxTentativas} tentativas`);
          return;
        }

        // PASSO 2: Chamar callback com dados atualizados da televisão
        try {
          callbackTelevisao(televisao);
        } catch (error) {
          console.error(`[SignalR] ❌ Erro ao executar callback de televisão ${televisaoId}:`, error);
        }
        
        // Também disparar evento genérico (para compatibilidade)
        this.handleEvent({
          type: 'televisao_updated',
          televisaoId: televisaoId,
          data: televisao
        });
        
        // NOTA: Renovação de token é feita automaticamente pelo timer de 2h e pelo health check
        // Não é necessário chamar RenovarToken() aqui
      } catch (error) {
        console.error(`[SignalR] ❌ Erro ao processar atualização da televisão ${televisaoId}:`, error);
      }
    });
    

    // Handler para receber atualizações de sequência
    this.connection.on('SequenciaAtualizada', (nomeSequencia: string, data: any) => {
      this.handleEvent({
        type: 'sequence_updated',
        nomeSequencia: nomeSequencia,
        data: data
      });
    });

    // ============================================
    // HANDLER: CodigoRealGerado (Quando código falso é validado e código real é gerado)
    // ============================================
    this.connection.on('CodigoRealGerado', (data: any) => {
      try {
        const codigoFalso = data.CodigoFalso || data.codigoFalso;
        const codigoReal = data.CodigoReal || data.codigoReal;
        const empresaNome = data.EmpresaNome || data.empresaNome || data.Empresa || data.empresa;
        
        if (!codigoFalso || !codigoReal) {
          return;
        }
        
        // Chamar callback se existir - passar código real E empresa
        const callback = this.codigoRealCallbacks.get(codigoFalso);
        if (callback) {
          callback(codigoReal, empresaNome);
        }
      } catch (error) {
        console.error('[SignalR] Erro ao processar CodigoRealGerado:', error);
      }
    });

    // ============================================
    // HANDLER: ProdutoAtualizado (Atualização de preços de produtos)
    // ============================================
    // IMPORTANTE: O SignalR apenas envia um SINAL genérico quando um produto é atualizado
    // O SignalR NÃO envia quais produtos foram atualizados
    // Seguir EXATAMENTE o padrão da TelevisaoAtualizada:
    // 1. Aguardar delay para backend processar
    // 2. Buscar preços atualizados via API (com retry)
    // 3. Chamar callback com os preços atualizados
    this.connection.on('ProdutoAtualizado', async (data: any) => {
      // Notificar todos os callbacks registrados para atualização de preços
      // Cada callback recebe os produtoIds da tela e busca preços atualizados
      this.priceUpdateCallbacks.forEach(async (callback, screenId) => {
        try {
          // CRÍTICO: Aguardar um pouco para garantir que o backend já processou a atualização
          // Isso evita pegar dados em cache do servidor (mesmo padrão da televisão)
          await new Promise(resolve => setTimeout(resolve, 500)); // 500ms de delay
          
          // O callback recebe os produtoIds da tela e faz a busca de preços
          // O callback já tem acesso aos produtoIds através do hook usePriceUpdate
          callback([]); // Array vazio - o callback vai buscar os produtos da tela atual
        } catch (error) {
          console.error(`[SignalR] ❌ Erro ao processar atualização de preços para tela ${screenId}:`, error);
        }
      });
    });

    // ============================================
    // HANDLER: TokenRenovado (OBRIGATÓRIO - Renovação automática de token)
    // ============================================
    // Este evento é enviado:
    // - Automaticamente conforme estratégia de renovação configurada no backend
    // - Quando você chama RenovarToken() manualmente
    this.connection.on('TokenRenovado', async (novoToken: string) => {
      if (!novoToken || typeof novoToken !== 'string') {
        console.error('[TokenRenovado] Token inválido recebido');
        return;
      }

      try {
        // Verificar se acabou de conectar (evitar reconexão imediata)
        const timeSinceConnection = Date.now() - this.lastSuccessfulConnectionTime;
        if (this.isRecentlyConnected || timeSinceConnection < this.MIN_TIME_BEFORE_FORCE_RECONNECT) {
          // Apenas atualizar o token, sem reconectar
          localStorage.setItem('auth_token', novoToken);
          apiService.setToken(novoToken);
          this.lastSuccessfulTokenRenewal = Date.now();
          this.resetTokenRenewalTimer();
          return;
        }

        // ATUALIZA O TOKEN NO STORAGE (usando a mesma chave que apiService usa)
        localStorage.setItem('auth_token', novoToken);
        
        // Atualizar no apiService (isso também salva em 'auth_token' internamente)
        apiService.setToken(novoToken);
        
        // Atualizar timestamp da última renovação bem-sucedida
        this.lastSuccessfulTokenRenewal = Date.now();
        
        // Resetar timer de renovação de token (2 horas)
        this.resetTokenRenewalTimer();
        
        // Sempre reconectar para renegociar autenticação com o token renovado.
        // Isso evita que a conexão atual continue válida com o token antigo.
        this.pendingReconnectAfterTokenRenewal = false;
        await this.forceReconnect();
      } catch (error) {
        this.pendingReconnectAfterTokenRenewal = false;
        console.error('[TokenRenovado] Erro ao atualizar token:', error);
      }
    });

    // Handler para reconexão
    this.connection.onreconnecting((error) => {
      if (this.isTV) {
      }
      this.reconnectAttempts++;
    });

    this.connection.onreconnected((connectionId) => {
      if (this.isTV) {
      }
      this.reconnectAttempts = 0;
      this.lastHeartbeatTime = Date.now();
      
      // Re-subscribir a todas as subscriptions ativas após reconexão
      // Para TVs: garantir re-subscription com retry
      if (this.isTV) {
        this.resubscribeAllWithRetry();
      } else {
        this.resubscribeAll();
      }
      
      // Para TVs: reiniciar heartbeat após reconexão
      if (this.isTV) {
        this.startHeartbeat();
      }
      
      // Para modo totem: reiniciar verificação de saúde após reconexão (apenas se não estiver ativa)
      // CRÍTICO: Não reiniciar se já estiver ativa para evitar múltiplas instâncias
      if (totemModeService.isTotemMode() && !this.isHealthCheckActive) {
        this.startConnectionHealthCheck();
      }
    });

    this.connection.onclose((error) => {
      // Verificar se é erro de autenticação
      const isAuthError = error?.message?.includes('401') || 
                         error?.message?.includes('403') ||
                         error?.message?.includes('Unauthorized') ||
                         error?.message?.includes('Forbidden');
      
      if (this.isTV) {
      }
      this.isConnecting = false;
      this.connection = null;
      
      // Parar heartbeat quando desconectar
      this.stopHeartbeat();
      
      // Parar verificação de saúde quando desconectar
      this.stopConnectionHealthCheck();

      // Reconectar automaticamente se não foi fechamento manual e não for erro de autenticação
      if (!this.isManualClose && !this.isIntentionalReconnect && options.autoReconnect !== false && !isAuthError) {
        this.scheduleReconnect(options);
      }
    });
  }

  /**
   * Solicita renovação de token ao backend via SignalR (fallback quando HTTP falha).
   * Usado apenas como fallback quando a renovação via HTTP endpoint falha.
   */
  private async requestTokenRenewal(reason: string): Promise<boolean> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn(`[SignalR] ⚠️ Não foi possível solicitar RenovarToken (${reason}) - conexão inativa`);
      return false;
    }

    try {
      await this.connection.invoke('RenovarToken');
      return true;
    } catch (error) {
      console.error(`[SignalR] ❌ Erro ao solicitar RenovarToken (${reason}):`, error);
      return false;
    }
  }

  /**
   * Inicia verificação periódica (heartbeat) da conexão para TVs
   * Verifica a cada 10 segundos se a conexão está ativa
   */
  /**
   * Heartbeat simplificado para TVs (complementa withAutomaticReconnect)
   * Apenas atualiza timestamp - withAutomaticReconnect já cuida da reconexão automática
   */
  private startHeartbeat(): void {
    if (!this.isTV) {
      return;
    }
    
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
        this.lastHeartbeatTime = Date.now();
      }
    }, 10000);
  }

  /**
   * Inicia verificação periódica de saúde da conexão e token (para modo totem)
   * Verifica a cada 5 minutos:
   * - Se a conexão SignalR está ativa
   * - Se o token está próximo de expirar (renova preventivamente)
   * - Se faz mais de 1h30 desde a última renovação (renova preventivamente)
   */
  private startConnectionHealthCheck(): void {
    // CRÍTICO: Evitar múltiplas instâncias
    if (this.isHealthCheckActive && this.connectionHealthCheckInterval) {
      return;
    }
    
    // Parar verificação anterior se existir
    this.stopConnectionHealthCheck();

    this.isHealthCheckActive = true;
    
    this.connectionHealthCheckInterval = setInterval(async () => {
      await this.performConnectionHealthCheck();
    }, this.CONNECTION_HEALTH_CHECK_INTERVAL_MS);
  }

  /**
   * Para a verificação periódica de saúde da conexão
   */
  private stopConnectionHealthCheck(): void {
    if (this.connectionHealthCheckInterval) {
      clearInterval(this.connectionHealthCheckInterval);
      this.connectionHealthCheckInterval = null;
      this.isHealthCheckActive = false;
    }
  }

  /**
   * Executa verificação de saúde da conexão e token (para modo totem)
   * Verifica se o token está próximo de expirar e renova preventivamente
   * NOTA: withAutomaticReconnect já cuida da reconexão do SignalR
   */
  private async performConnectionHealthCheck(): Promise<void> {
    if (!totemModeService.isTotemMode()) {
      this.stopConnectionHealthCheck();
      return;
    }

    // Verificar se o token está próximo de expirar ou se faz mais de 1h30 desde última renovação
    const currentToken = apiService.getToken();
    if (!currentToken) {
      return;
    }

    const isExpiring = authService.isTokenExpiringOrExpired(currentToken, 30); // 30 minutos de buffer
    const timeSinceLastRenewal = Date.now() - this.lastSuccessfulTokenRenewal;
    const oneAndHalfHours = 90 * 60 * 1000; // 1h30 em ms

    if (isExpiring || (this.lastSuccessfulTokenRenewal > 0 && timeSinceLastRenewal > oneAndHalfHours)) {
      
      try {
        // Tentar renovar via HTTP primeiro (fallback para /refresh endpoint)
        await authService.refreshToken(currentToken);
        this.lastSuccessfulTokenRenewal = Date.now();
        
        // Reconectar SignalR para aplicar novo token (accessTokenFactory usará o novo token)
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
          await this.forceReconnect();
        }
      } catch (error) {
        console.error('[SignalR] 🏥 [TOTEM] ❌ Erro ao renovar token preventivamente:', error);
        
        // Fallback: tentar via SignalR (evento TokenRenovado)
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
          await this.requestTokenRenewal('health check preventivo');
        }
      }
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Re-subscribe a todas as subscriptions com retry (para TVs)
   */
  private async resubscribeAllWithRetry(): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] 📺 [TV] ⚠️ Não conectado para re-subscription');
      return;
    }

    const subscriptionsArray = Array.from(this.subscriptions);
    
    for (const subscriptionId of subscriptionsArray) {
      const [type, id] = subscriptionId.split(':');
      
      // Verificar novamente a conexão antes de cada invoke
      if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
        console.warn('[SignalR] 📺 [TV] ⚠️ Conexão perdida durante re-subscription');
        return;
      }
      
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries) {
        try {
          if (type === 'screen') {
            await this.connection.invoke('JoinTelaGroup', parseInt(id));
            break;
          } else if (type === 'televisao') {
            await this.connection.invoke('JoinTelevisaoGroup', parseInt(id));
            break;
          } else if (type === 'sequence') {
            await this.connection.invoke('JoinSequenciaGroup', id);
            break;
          } else if (type === 'codigoFalso') {
            await this.connection.invoke('JoinCodigoFalsoGroup', id);
            break;
          }
        } catch (error) {
          retries++;
          console.warn(`[SignalR] 📺 [TV] ⚠️ Erro ao re-subscribir ${subscriptionId} (tentativa ${retries}/${maxRetries}):`, error);
          
          if (retries < maxRetries) {
            // Aguardar antes de tentar novamente
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            console.error(`[SignalR] 📺 [TV] ❌ Falha ao re-subscribir ${subscriptionId} após ${maxRetries} tentativas`);
          }
        }
      }
    }
    
  }


  /**
   * Agenda reconexão após desconexão
   */
  private scheduleReconnect(options: WebSocketOptions) {
    if (this.reconnectTimeout) {
      return; // Já agendado
    }

    const maxAttempts = options.maxReconnectAttempts ?? this.maxReconnectAttempts;
    if (this.reconnectAttempts >= maxAttempts) {
      console.error(`[SignalR] Máximo de tentativas de reconexão atingido (${maxAttempts})`);
      return;
    }

    this.reconnectAttempts++;
    const interval = options.reconnectInterval ?? this.reconnectInterval;
    
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect(options).catch(error => {
        console.error('[SignalR] Erro na reconexão:', error);
      });
    }, interval);
  }

  /**
   * Processa eventos recebidos e notifica handlers
   */
  private handleEvent(event: WebSocketEvent) {
    // Normalizar screenId se vier como objeto
    if ('screenId' in event && typeof event.screenId !== 'number') {
      const screenIdObj = event.screenId as any;
      if (screenIdObj && (screenIdObj.telaId || screenIdObj.id)) {
        (event as any).screenId = screenIdObj.telaId || screenIdObj.id;
      }
    }

    if (this.eventHandlers.size === 0) {
      return;
    }

    // Notificar todos os handlers registrados
    // Converter Set para Array para ter índice
    const handlersArray = Array.from(this.eventHandlers);
    handlersArray.forEach((handler, index) => {
      try {
        handler(event);
      } catch (error) {
        console.error(`[SignalR] Erro ao executar handler ${index + 1}:`, error);
      }
    });
  }

  /**
   * Subscribe para receber atualizações de uma tela
   * Usa grupos do SignalR para gerenciar subscriptions
   */
  async subscribeScreen(screenId: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Não conectado. Tentando conectar...');
      try {
        await this.connect();
      } catch (error) {
        console.error('[SignalR] Erro ao conectar para subscribe:', error);
        return;
      }
    }

    const subscriptionId = `screen:${screenId}`;
    if (this.subscriptions.has(subscriptionId)) {
      return;
    }

    this.subscriptions.add(subscriptionId);
    
    // Verificar novamente após possível reconexão
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.error('[SignalR] Conexão não disponível após tentativa de reconexão');
      this.subscriptions.delete(subscriptionId);
      return;
    }
    
    try {
      // SignalR usa grupos para gerenciar subscriptions
      // O backend deve ter um método JoinGroup no Hub
      await this.connection.invoke('JoinTelaGroup', screenId);
    } catch (error) {
      console.error(`[SignalR] Erro ao inscrever na tela ${screenId}:`, error);
      this.subscriptions.delete(subscriptionId);
    }
  }

  /**
   * Subscribe para receber atualizações de uma televisão
   */
  async subscribeTelevisao(televisaoId: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Não conectado. Tentando conectar...');
      try {
        await this.connect();
      } catch (error) {
        console.error('[SignalR] Erro ao conectar para subscribe:', error);
        return;
      }
    }

    const subscriptionId = `televisao:${televisaoId}`;
    if (this.subscriptions.has(subscriptionId)) {
      return;
    }

    this.subscriptions.add(subscriptionId);
    
    // Verificar novamente após possível reconexão
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.error('[SignalR] Conexão não disponível após tentativa de reconexão');
      this.subscriptions.delete(subscriptionId);
      return;
    }
    
    try {
      await this.connection.invoke('JoinTelevisaoGroup', televisaoId);
    } catch (error) {
      console.error(`[SignalR] ❌ Erro ao inscrever na televisão ${televisaoId}:`, error);
      console.error(`[SignalR] ❌ Verifique se o backend tem o método JoinTelevisaoGroup no Hub`);
      this.subscriptions.delete(subscriptionId);
    }
  }

  /**
   * Subscribe para receber atualizações de uma sequência
   */
  async subscribeSequence(nomeSequencia: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Não conectado. Tentando conectar...');
      try {
        await this.connect();
      } catch (error) {
        console.error('[SignalR] Erro ao conectar para subscribe:', error);
        return;
      }
    }

    const subscriptionId = `sequence:${nomeSequencia}`;
    if (this.subscriptions.has(subscriptionId)) {
      return;
    }

    this.subscriptions.add(subscriptionId);
    
    // Verificar novamente após possível reconexão
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.error('[SignalR] Conexão não disponível após tentativa de reconexão');
      this.subscriptions.delete(subscriptionId);
      return;
    }
    
    try {
      await this.connection.invoke('JoinSequenciaGroup', nomeSequencia);
    } catch (error) {
      console.error(`[SignalR] Erro ao inscrever na sequência ${nomeSequencia}:`, error);
      this.subscriptions.delete(subscriptionId);
    }
  }

  /**
   * Unsubscribe de uma tela
   */
  async unsubscribeScreen(screenId: number): Promise<void> {
    const subscriptionId = `screen:${screenId}`;
    this.subscriptions.delete(subscriptionId);
    
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveTelaGroup', screenId);
      } catch (error) {
        console.error(`[SignalR] Erro ao desinscrever da tela ${screenId}:`, error);
      }
    }
  }

  /**
   * Unsubscribe de uma televisão
   */
  async unsubscribeTelevisao(televisaoId: number): Promise<void> {
    const subscriptionId = `televisao:${televisaoId}`;
    this.subscriptions.delete(subscriptionId);
    
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveTelevisaoGroup', televisaoId);
      } catch (error) {
        console.error(`[SignalR] Erro ao desinscrever da televisão ${televisaoId}:`, error);
      }
    }
  }

  /**
   * Unsubscribe de uma sequência
   */
  async unsubscribeSequence(nomeSequencia: string): Promise<void> {
    const subscriptionId = `sequence:${nomeSequencia}`;
    this.subscriptions.delete(subscriptionId);
    
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveSequenciaGroup', nomeSequencia);
      } catch (error) {
        console.error(`[SignalR] Erro ao desinscrever da sequência ${nomeSequencia}:`, error);
      }
    }
  }

  // Callbacks para código real gerado - quando receber CodigoRealGerado via SignalR
  // Map<codigoFalso, callback> - callback recebe código real e empresa (opcional)
  private codigoRealCallbacks: Map<string, (codigoReal: string, empresaNome?: string) => void> = new Map();

  /**
   * Subscribe para receber código real quando código falso for validado
   * @param codigoFalso - Código falso de 5 caracteres gerado na TV
   * @param callback - Callback chamado quando código real for recebido (recebe código real e empresa opcional)
   */
  async subscribeCodigoFalso(codigoFalso: string, callback: (codigoReal: string, empresaNome?: string) => void): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] Não conectado. Tentando conectar...');
      try {
        await this.connect();
      } catch (error) {
        console.error('[SignalR] Erro ao conectar para subscribe código falso:', error);
        return;
      }
    }

    const subscriptionId = `codigoFalso:${codigoFalso}`;
    if (this.subscriptions.has(subscriptionId)) {
      // Se já está inscrito, apenas atualizar callback
      this.codigoRealCallbacks.set(codigoFalso, callback);
      return;
    }

    this.subscriptions.add(subscriptionId);
    this.codigoRealCallbacks.set(codigoFalso, callback);
    
    // Verificar novamente após possível reconexão
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.error('[SignalR] Conexão não disponível após tentativa de reconexão');
      this.subscriptions.delete(subscriptionId);
      this.codigoRealCallbacks.delete(codigoFalso);
      return;
    }
    
    try {
      await this.connection.invoke('JoinCodigoFalsoGroup', codigoFalso);
      console.log(`[SignalR] ✅ Inscrito no grupo CodigoFalso_${codigoFalso}`);
    } catch (error) {
      console.error(`[SignalR] ❌ Erro ao inscrever no código falso ${codigoFalso}:`, error);
      this.subscriptions.delete(subscriptionId);
      this.codigoRealCallbacks.delete(codigoFalso);
    }
  }

  /**
   * Unsubscribe de um código falso
   */
  async unsubscribeCodigoFalso(codigoFalso: string): Promise<void> {
    const subscriptionId = `codigoFalso:${codigoFalso}`;
    this.subscriptions.delete(subscriptionId);
    this.codigoRealCallbacks.delete(codigoFalso);
    
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      try {
        await this.connection.invoke('LeaveCodigoFalsoGroup', codigoFalso);
        console.log(`[SignalR] ✅ Desinscrito do grupo CodigoFalso_${codigoFalso}`);
      } catch (error) {
        console.error(`[SignalR] Erro ao desinscrever do código falso ${codigoFalso}:`, error);
      }
    }
  }

  /**
   * Re-subscribe a todas as subscriptions ativas (útil após reconexão)
   */
  private async resubscribeAll(): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    // Converter Set para Array para evitar problemas de iteração
    const subscriptionsArray = Array.from(this.subscriptions);
    
    for (const subscriptionId of subscriptionsArray) {
      const [type, id] = subscriptionId.split(':');
      
      // Verificar novamente a conexão antes de cada invoke
      if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
        return;
      }
      
      try {
        if (type === 'screen') {
          await this.connection.invoke('JoinTelaGroup', parseInt(id));
        } else if (type === 'televisao') {
          await this.connection.invoke('JoinTelevisaoGroup', parseInt(id));
        } else if (type === 'sequence') {
          await this.connection.invoke('JoinSequenciaGroup', id);
        } else if (type === 'codigoFalso') {
          await this.connection.invoke('JoinCodigoFalsoGroup', id);
        }
      } catch (error) {
        console.error(`[SignalR] Erro ao re-subscribir ${subscriptionId}:`, error);
      }
    }
  }

  /**
   * Registra um handler para eventos SignalR
   */
  onEvent(handler: WebSocketEventHandler): () => void {
    this.eventHandlers.add(handler);
    
    // Retorna função para remover o handler
    return () => {
      this.eventHandlers.delete(handler);
    };
  }

  /**
   * Remove um handler de eventos
   */
  offEvent(handler: WebSocketEventHandler): void {
    this.eventHandlers.delete(handler);
  }

  /**
   * Registra callback direto para atualizações de uma tela específica
   * Quando receber TelaAtualizada, faz GET automaticamente e chama o callback
   */
  onScreenUpdate(screenId: number, callback: (screen: Screen, telaAPI: TelaBanner) => void): () => void {
    this.screenUpdateCallbacks.set(screenId, callback);
    
    return () => {
      this.screenUpdateCallbacks.delete(screenId);
    };
  }

  /**
   * Remove callback direto de uma tela
   */
  offScreenUpdate(screenId: number): void {
    this.screenUpdateCallbacks.delete(screenId);
  }

  /**
   * Registra callback direto para atualizações de uma tela específica quando ela está em uma televisão
   * Quando receber TelaAtualizada, faz GET automaticamente e chama o callback
   * IMPORTANTE: A televisão recebe o mesmo sinal que a tela normal e aplica o estilo nas telas configuradas
   */
  onTelevisaoScreenUpdate(screenId: number, callback: (screen: Screen, telaAPI: TelaBanner) => void): () => void {
    this.televisaoScreenUpdateCallbacks.set(screenId, callback);
    
    return () => {
      this.televisaoScreenUpdateCallbacks.delete(screenId);
    };
  }

  /**
   * Remove callback direto de uma tela da televisão
   */
  offTelevisaoScreenUpdate(screenId: number): void {
    this.televisaoScreenUpdateCallbacks.delete(screenId);
  }

  /**
   * Registra callback direto para atualizações de uma televisão específica
   * Quando receber TelevisaoAtualizada, chama o callback com os dados recebidos diretamente
   * IMPORTANTE: Os dados já vêm completos no evento (dsTelasCadastradas, dsConfiguracoes, etc)
   */
  onTelevisaoUpdate(televisaoId: number, callback: (data: any) => void): () => void {
    this.televisaoUpdateCallbacks.set(televisaoId, callback);
    
    return () => {
      this.televisaoUpdateCallbacks.delete(televisaoId);
    };
  }

  /**
   * Remove callback direto de uma televisão
   */
  offTelevisaoUpdate(televisaoId: number): void {
    this.televisaoUpdateCallbacks.delete(televisaoId);
  }

  /**
   * Registra callback para atualização de preços de produtos em uma tela específica
   * Quando receber ProdutoAtualizado, chama o callback que deve buscar e atualizar os preços
   * @param screenId - ID da tela que precisa ter preços atualizados
   * @param callback - Função que será chamada quando houver atualização de preços
   * @returns Função para remover o callback
   */
  onPriceUpdate(screenId: number, callback: (produtoIds: number[]) => void): () => void {
    this.priceUpdateCallbacks.set(screenId, callback);
    
    return () => {
      this.priceUpdateCallbacks.delete(screenId);
    };
  }

  /**
   * Remove callback de atualização de preços de uma tela
   */
  offPriceUpdate(screenId: number): void {
    this.priceUpdateCallbacks.delete(screenId);
  }

  /**
   * Inicia timer de renovação de token e reconexão preventiva (2 horas)
   * 
   * CRÍTICO: Este timer garante que tanto o TOKEN quanto o SIGNALR sejam renovados/reconectados
   * a cada 2 horas, mantendo-os sempre alinhados e sincronizados.
   * 
   * Fluxo a cada 2 horas:
   * 1. Renova o token via HTTP (ou SignalR como fallback)
   * 2. Reconecta o SignalR para aplicar o novo token
   * 3. Reinicia o timer para o próximo ciclo (ciclo infinito)
   * 
   * Isso garante que:
   * - Token nunca expira (renovado preventivamente)
   * - SignalR nunca fica com token antigo (reconectado com novo token)
   * - Ambos estão sempre sincronizados (mesmo ciclo de 2 horas)
   */
  private startTokenRenewalTimer(): void {
    // CRÍTICO: Lock para evitar múltiplas chamadas simultâneas
    if (this.tokenRenewalTimerLock) {
      return;
    }
    
    // CRÍTICO: Se já existe um timer ativo, NÃO criar outro
    if (this.isTokenRenewalTimerActive && this.tokenRenewalTimer) {
      return;
    }
    
    // Ativar lock
    this.tokenRenewalTimerLock = true;
    
    // Limpar timer anterior se existir (garantir limpeza completa)
    if (this.tokenRenewalTimer) {
      clearTimeout(this.tokenRenewalTimer);
      this.tokenRenewalTimer = null;
    }
    
    // Marcar como ativo ANTES de criar o timer
    this.isTokenRenewalTimerActive = true;
    
    const intervalHours = this.TOKEN_RENEWAL_INTERVAL_MS / (60 * 60 * 1000);
    const timestamp = new Date().toISOString();
    
    this.tokenRenewalTimer = setTimeout(async () => {
      // Liberar lock e marcar como inativo IMEDIATAMENTE para evitar múltiplas execuções
      this.tokenRenewalTimerLock = false;
      this.isTokenRenewalTimerActive = false;
      this.tokenRenewalTimer = null;
      
      // Recalcular intervalHours para garantir que usa o valor atual
      const currentIntervalHours = this.TOKEN_RENEWAL_INTERVAL_MS / (60 * 60 * 1000);
      const expireTimestamp = new Date().toISOString();
      
      try {
        // PASSO 1: Tentar renovar token via HTTP
        // O backend agora usa lógica robusta para encontrar o código da TV em múltiplos claims
        // Então tentamos sempre via HTTP primeiro, e se falhar, usamos SignalR como fallback
        const currentToken = apiService.getToken();
        let tokenRenovado = false;
        
        if (currentToken) {
          // Tentar renovar via HTTP primeiro (backend decide se é token válido)
          try {
            await authService.refreshToken(currentToken);
            tokenRenovado = true;
          } catch (error: any) {
            console.error('[SignalR] ❌ Erro ao renovar token via HTTP:', error);
            
            // Verificar se é erro específico de "não é token de TV" ou erro genérico
            const errorMessage = error.message || '';
            const isTokenInvalido = errorMessage.includes('não foi possível extrair o código') || 
                                   errorMessage.includes('Token inválido') ||
                                   errorMessage.includes('400');
            
            if (isTokenInvalido) {
              // Token não é de TV ou não pode ser renovado via HTTP
              
              // FALLBACK: Tentar via SignalR
              if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
                try {
                  await this.requestTokenRenewal('fallback após erro HTTP');
                  console.log('[SignalR] ✅ Renovação via SignalR solicitada como fallback');
                  tokenRenovado = true; // Assumir que será renovado via SignalR
                } catch (signalRError) {
                  console.error('[SignalR] ❌ Erro ao solicitar renovação via SignalR:', signalRError);
                }
              }
            } else {
              // Erro de rede ou outro problema - tentar SignalR também
              if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
                try {
                  await this.requestTokenRenewal('fallback após erro de rede');
                  console.log('[SignalR] ✅ Renovação via SignalR solicitada como fallback');
                } catch (signalRError) {
                  console.error('[SignalR] ❌ Erro ao solicitar renovação via SignalR:', signalRError);
                }
              }
            }
            // Continuar mesmo se falhar - pode tentar reconectar com token atual
          }
        }

        // PASSO 2: SEMPRE reconectar SignalR a cada 2 horas (alinhado com renovação do token)
        // CRÍTICO: Mesmo se a renovação do token falhar, reconectamos o SignalR para garantir
        // que ele use o token mais recente disponível e mantenha a conexão ativa
        
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
          // Se está conectado, forçar reconexão para aplicar novo token (ou token atualizado)
          await this.forceReconnect();
        } else {
          // Se não está conectado, tentar conectar
          await this.connect();
        }

        // Reiniciar timer para próximo ciclo
        setTimeout(() => {
          if (!this.isTokenRenewalTimerActive) {
            this.startTokenRenewalTimer();
          }
        }, 100);
      } catch (error) {
        console.error('[SignalR] ❌ Erro no ciclo de renovação de token:', error);
        
        // MESMO COM ERRO: Tentar reconectar SignalR para manter conexão ativa
        // Isso garante que o SignalR seja reconectado a cada 2h mesmo se a renovação do token falhar
        try {
          if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            await this.forceReconnect();
          } else {
            await this.connect();
          }
        } catch (reconnectError) {
          console.error('[SignalR] ❌ Erro ao reconectar SignalR após falha na renovação:', reconnectError);
        }
        
        // Tentar novamente após o intervalo configurado (ciclo infinito)
        setTimeout(() => {
          if (!this.isTokenRenewalTimerActive) {
            this.startTokenRenewalTimer();
          }
        }, this.TOKEN_RENEWAL_INTERVAL_MS);
      }
    }, this.TOKEN_RENEWAL_INTERVAL_MS);
    
    // Liberar lock após criar o timer
    this.tokenRenewalTimerLock = false;
  }

  /**
   * Para o timer de renovação de token
   */
  private stopTokenRenewalTimer(): void {
    if (this.tokenRenewalTimer) {
      clearTimeout(this.tokenRenewalTimer);
      this.tokenRenewalTimer = null;
    }
    this.isTokenRenewalTimerActive = false;
    this.tokenRenewalTimerLock = false;
  }

  /**
   * Reseta o timer de renovação de token
   * Usado quando recebe TokenRenovado para estender o tempo de conexão
   */
  private resetTokenRenewalTimer(): void {
    const intervalHours = this.TOKEN_RENEWAL_INTERVAL_MS / (60 * 60 * 1000);
    this.startTokenRenewalTimer();
  }

  /**
   * Reativa/reconecta a conexão SignalR
   * Verifica se está conectado e reconecta se necessário
   */
  private async reactivateConnection(): Promise<void> {
    if (!this.connection) {
      // Se não tem conexão, conectar
      try {
        await this.connect();
      } catch (error) {
        console.error('[SignalR] ❌ Erro ao reativar conexão:', error);
      }
      return;
    }

    const state = this.connection.state;
    
    if (state === signalR.HubConnectionState.Connected) {
      // Já está conectado - apenas resetar timer
      return;
    }

    if (state === signalR.HubConnectionState.Disconnected) {
      // Desconectado - reconectar
      try {
        await this.connection.start();
        
        // Re-subscribir após reconexão
        await this.resubscribeAll();
      } catch (error) {
        console.error('[SignalR] ❌ Erro ao reconectar:', error);
      }
      return;
    }

      // Se está reconnecting, aguardar
      if (state === signalR.HubConnectionState.Reconnecting) {
        return;
      }
  }

  /**
   * Força reconexão controlada para renegociar autenticação com token atualizado.
   * CRÍTICO: Usa lock para evitar múltiplas chamadas simultâneas que criariam múltiplas conexões.
   */
  private async forceReconnect(): Promise<void> {
    // CRÍTICO: Lock para evitar múltiplas chamadas simultâneas
    if (this.isForceReconnecting) {
      return;
    }

    // Verificar se já está conectando ou reconectando
    if (this.isConnecting) {
      return;
    }

    if (this.connection && this.connection.state === signalR.HubConnectionState.Reconnecting) {
      return;
    }
    
    // Proteção: não reconectar se acabou de conectar (evitar loop)
    const timeSinceConnection = Date.now() - this.lastSuccessfulConnectionTime;
    if (this.isRecentlyConnected || timeSinceConnection < this.MIN_TIME_BEFORE_FORCE_RECONNECT) {
      return;
    }

    this.isForceReconnecting = true;
    this.isIntentionalReconnect = true;
    
    try {
      // Se está conectado, parar conexão atual
      if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
        await this.connection.stop();
        // Aguardar um pouco para garantir que a conexão foi fechada
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Aguardar um pouco se ainda estiver conectando
      if (this.isConnecting) {
        let waitCount = 0;
        while (this.isConnecting && waitCount < 10) {
          await new Promise(resolve => setTimeout(resolve, 200));
          waitCount++;
        }
      }

      // O onclose zera this.connection; por isso recriamos via connect().
      await this.connect({
        autoReconnect: true,
        reconnectInterval: this.reconnectInterval,
        maxReconnectAttempts: this.maxReconnectAttempts
      });
    } catch (error) {
      console.error('[SignalR] ❌ Erro na reconexão forçada:', error);
      throw error;
    } finally {
      this.isForceReconnecting = false;
      this.isIntentionalReconnect = false;
    }
  }

  /**
   * Desconecta do SignalR
   */
  async disconnect(): Promise<void> {
    this.isManualClose = true;
    
    // Parar heartbeat
    this.stopHeartbeat();
    
    // Parar verificação de saúde
    this.stopConnectionHealthCheck();
    
    // Parar timer de renovação de token
    this.stopTokenRenewalTimer();
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[SignalR] Erro ao desconectar:', error);
      }
      this.connection = null;
    }
    
    this.subscriptions.clear();
    this.eventHandlers.clear();
  }

  /**
   * Verifica se está conectado
   */
  isConnected(): boolean {
    return this.connection !== null && this.connection.state === signalR.HubConnectionState.Connected;
  }
}

// Singleton
export const websocketService = new WebSocketService();
